import { useCallback, useEffect, useState } from 'react'

import { authApi, type SilkAuthConfig } from '@/api'
import { validateTenantMatch } from '@/config/tenant-site'
import {
  AUTH_CONFIG_INVALIDATE_EVENT,
  clearAuthConfigCache,
  getAuthConfigCache,
  getAuthConfigInflight,
  setAuthConfigCache,
  setAuthConfigInflight,
} from '@/shared/lib/auth-config-cache'

async function fetchConfig(force = false): Promise<SilkAuthConfig> {
  if (!force && getAuthConfigCache()) return getAuthConfigCache()!

  if (!force && getAuthConfigInflight()) return getAuthConfigInflight()!

  const promise = authApi
    .config()
    .then((res) => {
      const mismatch = validateTenantMatch(res.tenant_code, res.tenant_mode)
      if (mismatch) {
        setAuthConfigInflight(null)
        throw new Error(mismatch)
      }
      setAuthConfigCache(res)
      setAuthConfigInflight(null)
      return res
    })
    .catch((err) => {
      setAuthConfigInflight(null)
      throw err
    })

  setAuthConfigInflight(promise)
  return promise
}

const VISIBILITY_REFRESH_MS = 60_000
let lastVisibilityRefreshAt = 0

/** 拉取公开鉴权配置(短信开关、注册必填项),进程内缓存 + inflight 去重 */
export function useAuthConfig() {
  const [config, setConfig] = useState<SilkAuthConfig | null>(() => getAuthConfigCache())
  const [loading, setLoading] = useState(!getAuthConfigCache())
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (force = false) => {
    if (!force && getAuthConfigCache()) {
      setConfig(getAuthConfigCache())
      setLoading(false)
      return getAuthConfigCache()!
    }
    setLoading(true)
    setError(null)
    try {
      const next = await fetchConfig(force)
      setConfig(next)
      return next
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'failed to load auth config'
      setError(msg)
      throw e
    } finally {
      setLoading(false)
    }
  }, [])

  const reload = useCallback(async () => {
    clearAuthConfigCache()
    return load(true)
  }, [load])

  useEffect(() => {
    const cached = getAuthConfigCache()
    if (cached) {
      setConfig((prev) => (prev === cached ? prev : cached))
      setLoading((prev) => (prev ? false : prev))
      return
    }
    void load(false).catch(() => {
      /* error state 已设置 */
    })
  }, [load])

  useEffect(() => {
    const onInvalidate = () => {
      void reload().catch(() => undefined)
    }
    window.addEventListener(AUTH_CONFIG_INVALIDATE_EVENT, onInvalidate)
    return () => window.removeEventListener(AUTH_CONFIG_INVALIDATE_EVENT, onInvalidate)
  }, [reload])

  /** 切回页面时刷新租户配置(功能模块/邀请码等后台变更),60s 内不重复强刷 */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastVisibilityRefreshAt < VISIBILITY_REFRESH_MS) return
      lastVisibilityRefreshAt = now
      void reload().catch(() => undefined)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [reload])

  return { config, loading, error, reload }
}
